import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { createCheckout } from "@/lib/api";
import { formatINR } from "@/lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, count, clear } = useCart();
  const [loading, setLoading] = useState(false);

  const onCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { url } = await createCheckout({
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        origin_url: window.location.origin,
      });
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast("Checkout could not begin. Try once more.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-bone border-l border-hairline rounded-none w-full sm:max-w-md p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-8 pb-4 border-b border-hairline">
          <SheetTitle asChild>
            <div className="flex items-baseline justify-between">
              <div>
                <p className="overline mb-2 text-left">The Bag</p>
                <h2 className="font-display text-3xl text-ink font-light leading-none text-left">
                  {count} {count === 1 ? "object" : "objects"}
                </h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-charcoal hover:text-ink"
                aria-label="Close cart"
                data-testid="close-cart"
              >
                <X size={18} />
              </button>
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Your bag of considered objects, with quantity controls and a checkout button.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <p className="overline mb-3 text-stone">Empty, for now</p>
              <p className="font-display italic text-2xl text-charcoal mb-6">
                Bring something quiet home.
              </p>
              <Link
                to="/shop"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase font-medium text-ink link-underline"
              >
                Browse the atelier <ArrowUpRight size={12} />
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-hairline">
              {items.map((it) => (
                <li key={it.slug} className="py-5 flex gap-4" data-testid={`cart-item-${it.slug}`}>
                  <div className="w-20 h-24 bg-clay overflow-hidden flex-shrink-0">
                    <img src={it.cover_image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="overline mb-1">{it.category}</p>
                    <h3 className="font-display text-lg text-ink leading-tight">{it.name}</h3>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 border border-hairline px-2 py-1">
                        <button
                          onClick={() => setQty(it.slug, it.qty - 1)}
                          aria-label="Decrease"
                          data-testid={`cart-minus-${it.slug}`}
                          className="text-charcoal hover:text-ink"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="font-mono text-xs w-5 text-center">{it.qty}</span>
                        <button
                          onClick={() => setQty(it.slug, it.qty + 1)}
                          aria-label="Increase"
                          data-testid={`cart-plus-${it.slug}`}
                          className="text-charcoal hover:text-ink"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <p className="font-mono text-sm text-ink">{formatINR(it.price * it.qty)}</p>
                    </div>
                    <button
                      onClick={() => remove(it.slug)}
                      data-testid={`cart-remove-${it.slug}`}
                      className="overline mt-3 text-stone hover:text-ink"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-hairline px-6 py-6 bg-bone">
            <div className="flex items-baseline justify-between mb-1">
              <p className="overline">Subtotal</p>
              <p className="font-display text-2xl text-ink">{formatINR(subtotal)}</p>
            </div>
            <p className="text-xs text-stone mb-5">Shipping & tax calculated at checkout.</p>
            <button
              onClick={onCheckout}
              disabled={loading}
              data-testid="cart-checkout"
              className="w-full flex items-center justify-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink py-5 hover:bg-moss transition-colors disabled:opacity-50"
            >
              {loading ? "Opening checkout…" : "Proceed to checkout"} {!loading && <ArrowUpRight size={14} />}
            </button>
            <button
              onClick={clear}
              data-testid="cart-clear"
              className="w-full mt-3 text-[0.7rem] tracking-[0.22em] uppercase font-medium text-stone hover:text-ink"
            >
              Empty the bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
